import { useAuthState } from "@/zustand/AuthState";
import { useVersionGateState } from "@/zustand/VersionGateState";
import { isVersionOutdated } from "@/lib/versionCheck";
import { useSubscriptionState } from "@/zustand/SubscriptionState";

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { ApiEndpoints } from "./apiEndpoints";
import { ApiError, AppError, isAppError } from "./apiError";
import { scheduleSyncRequest } from "@/indexedDB/services/syncScheduler";
// Import the breadcrumb sink directly (not the barrel) to keep this hot,
// widely-imported module free of the full error-reporting graph.
import { addBreadcrumb } from "@/components/error-boundary/breadcrumbs";

interface QueuedRequest {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}

const client = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Force a bearer-token refresh and resolve with the new access token.
 *
 * Shares the SAME `isRefreshing`/`failedQueue` lock as the 401 response
 * interceptor below, so a refresh triggered here and one triggered by an API
 * 401 COALESCE into a single `/auth/refresh` call — never two in parallel (which
 * could trip single-use-refresh-token reuse detection). The `isRefreshing` guard
 * and set are synchronous (no await between them), so two callers can't both
 * pass the guard on one tick.
 *
 * Added for the service-worker video stream: a stream 401 happens inside the SW
 * and never reaches the response interceptor, so a long playback would otherwise
 * die at the token TTL with no refresh. This lets the stream hook force the same
 * refresh the app already does everywhere else.
 *
 * Deliberately does NOT call `resetAuthState()` on failure (unlike the
 * interceptor): a stream-triggered refresh must not log the user out mid-video.
 * It only drains the shared queue so nothing hangs; logout stays owned by the
 * interceptor, on the next real API 401.
 */
export function refreshAccessToken(): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({
        resolve: (token) =>
          token ? resolve(token) : reject(new Error("Token refresh failed")),
        reject,
      });
    });
  }

  const refreshToken =
    useAuthState.getState().refreshToken || useAuthState.getState().tk;
  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token available"));
  }

  isRefreshing = true;
  return axios
    .post(ApiEndpoints.REFRESH_TOKEN, { refreshToken })
    .then((refreshResponse) => {
      const { accessToken, refreshToken: newRefreshToken } =
        refreshResponse.data;
      useAuthState.getState().setToken(accessToken);
      useAuthState.getState().setRefreshToken(newRefreshToken);
      client.defaults.headers.Authorization = `Bearer ${accessToken}`;
      processQueue(null, accessToken);
      return accessToken as string;
    })
    .catch((refreshError) => {
      // Drain waiters (so a queued interceptor request doesn't hang), but leave
      // logout to the interceptor — see the doc comment above.
      processQueue(refreshError, null);
      throw refreshError;
    })
    .finally(() => {
      isRefreshing = false;
    });
}

// Add a request interceptor to include authentication token
client.interceptors.request.use(
  async (config) => {
    const token = useAuthState.getState().tk;
    const username = useAuthState.getState().username;
    // TO DO => better check auth status
    if (token && username) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      config.headers.delete("Content-Type");
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Check X-Min-Client-Version header on every successful response
client.interceptors.response.use(
  (response) => {
    const minVersionHeader = response.headers["x-min-client-version"];
    const minVersion =
      typeof minVersionHeader === "string" ? minVersionHeader.trim() : null;
    if (minVersion && isVersionOutdated(minVersion)) {
      useVersionGateState.getState().setUpdateRequired();
    }
    return response;
  },
  (error) => Promise.reject(error),
);

// Add a response interceptor to handle token refresh
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      let refreshToken = useAuthState.getState().refreshToken;
      const accessToken = useAuthState.getState().tk;
      if (!refreshToken) {
        refreshToken = accessToken;
      }
      if (!refreshToken) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queued requests are deliberately NOT marked _retry (2026-08-17
        // review) — this matches main: a replay that 401s again may re-enter
        // refresh and self-heal. The account-deletion DELETE needs no
        // stricter bound: a replayed DELETE returns 404 Account.NotFound,
        // which deleteOwnUserAccount treats as success.
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            (
              originalRequest as { onBeforeRetry?: () => void }
            ).onBeforeRetry?.();
            return client(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(ApiEndpoints.REFRESH_TOKEN, {
          refreshToken,
        });

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          refreshResponse.data;

        useAuthState.getState().setToken(newAccessToken);
        useAuthState.getState().setRefreshToken(newRefreshToken);

        client.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        (originalRequest as { onBeforeRetry?: () => void }).onBeforeRetry?.();
        return client(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        useAuthState.getState().resetAuthState();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    // 403 Stripe plan feature restriction — signal the UI to show an upgrade prompt
    if (error.response?.status === 403) {
      const errorTitle = (error.response?.data as { title?: string })?.title;
      if (errorTitle === "Stripe.PlanFeatureRestricted") {
        useSubscriptionState.getState().setPlanRestricted(true);
      }
    }

    return Promise.reject(error);
  },
);

// Breadcrumb interceptor — registered LAST so it runs after the refresh
// interceptor above. A 401 that is transparently recovered by token refresh
// resolves to a successful response and never reaches this rejected handler,
// so only errors that actually survive (terminal failures) are recorded.
// Method + path + status only — never request/response bodies.
client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status ?? 0;
    const method = (error.config?.method ?? "get").toUpperCase();
    const path = (error.config?.url ?? "").split("?")[0];
    addBreadcrumb("network", `${method} ${path} failed`, { status });
    return Promise.reject(error);
  },
);

/**
 * Per-call options for {@link requestApi}.
 *
 * `retry429` opts a request into the shared sync scheduler (token-bucket pacing
 * + shared 429 cooldown + bounded retry). Use it ONLY for idempotent read-like
 * sync calls — NEVER for sends/edits/uploads/membership changes. Auto-retrying a
 * non-idempotent write would duplicate the side effect.
 */
export interface RequestApiOptions {
  retry429?: boolean;
}

export const requestApi = async (
  options: AxiosRequestConfig,
  { retry429 }: RequestApiOptions = {},
): Promise<AxiosResponse> => {
  const onSuccess = (response: AxiosResponse) => response;
  const onError = async (error: AxiosError) => {
    let errorMessage: string;
    if (error.response?.data) {
      if (typeof error.response.data === "string") {
        // Preserve status code for callers that need to distinguish failure modes.
        throw new ApiError(
          error.response.data,
          undefined,
          error.response.status,
        );
      } else if (isAppError(error.response.data)) {
        throw new ApiError<AppError>(
          error.response.data.detail,
          {
            ...error.response.data,
            title: `errors.${error.response.data.title}`,
          },
          error.response.status,
        );
      } else {
        errorMessage = JSON.stringify(error.response.data);
      }
    } else {
      errorMessage = error.message || "Request failed";
    }
    // Error handling
    // if (error.response) {
    //   // Server responded with a status other than 200
    //   console.error("Response data:", error.response.data);
    // } else if (error.request) {
    //   // Request was made but no response received
    //   console.error("Request data:", error.request);
    // } else {
    //   // Something happened in setting up the request
    //   console.error("Error message:", error.message);
    // }
    throw new Error(errorMessage);
  };

  try {
    // When opted in, run the raw client call through the shared sync scheduler
    // so it sees the live AxiosError (status + Retry-After headers) on a 429,
    // BEFORE `onError` transforms it into an ApiError/Error below.
    const response = retry429
      ? await scheduleSyncRequest(() => client(options))
      : await client(options);
    return onSuccess(response);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return onError(error);
    }
    throw error;
  }
};

export const requestBlob = async (
  url: string,
  signal?: AbortSignal,
  onDownloadProgress?: (e: { loaded: number; total?: number }) => void,
): Promise<Blob | null> => {
  if (!url) {
    return null;
  }

  const onSuccess = (response: AxiosResponse) => response.data;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onError = async (error: AxiosError) => {
    // const errorMessage = error.message || "Request failed";
    // if (error.response) {
    //   console.error("Response data:", error.response.data);
    // } else if (error.request) {
    //   console.error("Request data:", error.request);
    // } else {
    //   console.error("Error message:", errorMessage);
    // }
    return null; // Return null if the request fails
  };

  try {
    const response = await axios({
      url: url,
      method: "GET",
      responseType: "blob",
      headers: {},
      withCredentials: false,
      // When aborted, axios throws a CanceledError (an AxiosError), so onError
      // maps it to null — the download stops instead of running to completion.
      signal,
      onDownloadProgress,
    });
    return onSuccess(response);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return onError(error);
    }
    throw error;
  }
};
